"use client";
import {
  // eslint-disable-next-line no-restricted-imports
  useRouter as useNextRouter,
} from "next/navigation";
import { useAtom } from "jotai";
import {
  AppRouterInstance,
  NavigateOptions,
} from "next/dist/shared/lib/app-router-context.shared-runtime";

import { useRouterChange } from "./useRouterChange";
import { progressAtom } from "@/hooks/headerHooks";
import { useLang, useDefaultLocale } from "@/providers/langProvider";
import { useRemoveLangFromPath } from "@/hooks/useRemoveLangFromPath";
import { i18n } from "../../../../../i18n-config";

interface CustomNavigateOptions extends NavigateOptions {
  saveScrollPosition?: boolean;
}

/**
 * Custom router hook that wraps Next.js router with i18n locale handling
 * and scroll position management
 */
export const useRouter = (): AppRouterInstance => {
  const nextRouter = useNextRouter();
  const routerChange = useRouterChange();
  const [, setProgress] = useAtom(progressAtom);
  const currentLang = useLang();
  // Server-provided store default locale (basic_language) — the SAME source <Link>
  // uses to build hrefs. Using it here keeps the prefetched URL identical to the
  // navigated URL, so warming a product route actually hits on tap.
  const defaultLocale = useDefaultLocale();
  const removeLangFromPath = useRemoveLangFromPath();

  /**
   * Adds the current locale to the href if it's missing
   * @param href - The target URL
   * @returns URL with locale prefix
   */
  const addLocaleToHref = (href: string): string => {
    const isExternalUrl = href.startsWith("http");
    if (isExternalUrl) return href;

    const hasLocalePrefix = i18n.locales.find(
      (locale: string) =>
        href.startsWith(`/${locale}/`) || href === `/${locale}`,
    );

    if (hasLocalePrefix) {
      if (hasLocalePrefix === defaultLocale) {
        const stripped = href === `/${hasLocalePrefix}` ? "/" : href.replace(new RegExp(`^/${hasLocalePrefix}`), "");
        return stripped || "/";
      }
      return href;
    }

    // Normalize path and add current locale
    const normalizedPath = removeLangFromPath(href, false);
    const isExternalAfterNormalization = normalizedPath.startsWith("http");

    if (isExternalAfterNormalization) {
      return normalizedPath;
    }

    if (currentLang === defaultLocale) {
      return normalizedPath || "/";
    }

    return `/${currentLang}${normalizedPath === "/" ? "" : normalizedPath}`;
  };

  /**
   * Navigate to a new route with locale handling
   */
  const push = (href: string, options?: CustomNavigateOptions) => {
    const localizedHref = addLocaleToHref(href);

    routerChange.setRouterChange(localizedHref);

    // Clear scroll position unless explicitly preserved
    if (!options?.saveScrollPosition) {
      sessionStorage.removeItem("scrollPosition");
      sessionStorage.removeItem("scrollRestorePath");
    }

    nextRouter.push(localizedHref, options);
  };

  /**
   * Replace current route with locale handling
   */
  const replace = (href: string, options?: NavigateOptions) => {
    const localizedHref = addLocaleToHref(href);

    routerChange.setRouterChange(localizedHref);
    nextRouter.replace(localizedHref, options);
  };

  /**
   * Navigate back in history and show progress indicator
   */
  const back = () => {
    setProgress(true);
    nextRouter.back();
  };

  return {
    push,
    replace,
    back,
    refresh: nextRouter.refresh,
    prefetch: (href, options) => nextRouter.prefetch(addLocaleToHref(href), options),
    forward: nextRouter.forward,
  };
};
